<template>
    <NuxtLayout name="default">
        <template v-if="redirectPath" #header-left>
            <VJoyIcon class="registration__go-back" name="cross" @click="back()" />
        </template>
        <template #header-right>
            <VJoyButton size="small" variant="secondary" :href="signinHref" rel="nofollow" data-testid="who-are-you-login-cta">
                {{ $t('registration.identity.choice.header.cta.login') }}
            </VJoyButton>
        </template>
        <main class="identity-choice">
            <Highlight v-if="redirectPath"><span v-html="$t('registration.identity.choice.title.v2.full.advantage')"></span></Highlight>
            <Highlight v-else><span v-html="$t('registration.identity.choice.title.v2')"></span></Highlight>
            <div class="identity-choice-cards">
                <div class="identity-choice-cards__item">
                    <VJoyPanel>
                        <template #panel-body>
                            <div class="identity-choice-cards__img">
                                <MaltImg :src="getBrandIllustration('search-red.svg?vh=258174')" width="80" alt="" />
                            </div>
                            <div class="identity-choice-cards__content">
                                <h2>{{ $t('registration.identity.choice.company.title') }}</h2>
                                <p>{{ $t('registration.identity.choice.company.subtitle') }}</p>
                            </div>
                        </template>
                    </VJoyPanel>
                    <a :href="clientSignupHref" data-testid="client-identity-choice" rel="nofollow" />
                </div>
                <div class="identity-choice-cards__item">
                    <VJoyPanel>
                        <template #panel-body>
                            <div class="identity-choice-cards__img">
                                <MaltImg :src="getBrandIllustration('bust-digital-worker-woman-red.svg?vh=a3b145')" width="80" alt="" />
                            </div>
                            <div class="identity-choice-cards__content">
                                <h2>{{ $t('registration.identity.choice.freelancer.title') }}</h2>
                                <p>{{ $t('registration.identity.choice.freelancer.subtitle') }}</p>
                            </div>
                        </template>
                    </VJoyPanel>
                    <a href="/profile/freelancer-signup" data-testid="freelancer-identity-choice" rel="nofollow" />
                </div>
            </div>
            <p class="identity-choice__link joy-font-size-primary-400">
                <VJoyIcon name="rocket" />
                {{ $t('registration.identity.choice.strategy.title') }}
                <a href="/c/maltstrategy/consultants">{{ $t('registration.identity.choice.strategy.link') }}</a>
            </p>
            <p class="identity-choice__link joy-font-size-primary-200">
                {{ $t('registration.identity.choice.text.login') }}
                <a :href="signinHref" rel="nofollow">{{ $t('registration.identity.choice.link.login') }}</a>
            </p>
        </main>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {navigateTo, useRequestURL} from '#app';
    import {MaltImg} from '#components';
    import {
        definePageMeta,
        OpenGraphMetaTag,
        TwitterMetaTags,
        useDataTracking,
        useHead,
        useOpenGraphMetaTags,
        useSeoMeta,
        useSignupAttributesPropagation,
        useTranslation,
        useTwitterMetaTags,
    } from '#imports';
    import {getBrandIllustration} from '@malt/asset-utils';
    import {VJoyButton, VJoyIcon, VJoyPanel} from '@maltjoy/core-vue';
    import Highlight from '~/components/Highlight.vue';

    // Here, we want to use layouts/default.vue as a component (because it contains named slots)
    definePageMeta({
        layout: false,
    });

    const {t} = useTranslation();
    const title = t('registration.identity.choice.meta.title');

    const currentUrl = useRequestURL();
    const redirectPath = currentUrl.searchParams.get('redirect') || undefined;

    useSeoMeta({title});
    useOpenGraphMetaTags(new OpenGraphMetaTag({title, t}));
    useTwitterMetaTags(new TwitterMetaTags({card: 'summary', title, t}));

    const {determineClientSignupParameters, toQueryString} = useSignupAttributesPropagation();
    const signupParameters = determineClientSignupParameters();
    const signinHref = `/signin${toQueryString(signupParameters)}`;
    // Please keep the trailing slash because of https://github.com/nuxt/nuxt/issues/21872
    const clientSignupHref = `/signup-client/${toQueryString(signupParameters)}`;

    const {track} = useDataTracking();
    track('signup_client_funnel_step_viewed', {
        funnel_step: 'who-are-you',
        ...(signupParameters.pageSource ? {page_source: signupParameters.pageSource} : {}),
        ...(signupParameters.componentSource ? {component_source: signupParameters.componentSource} : {}),
    });

    function back() {
        if (document.referrer && document.referrer.includes(window.location.host)) {
            history.back();
        } else {
            navigateTo('/', {external: true});
        }
    }

    useHead({
        link: [
            {
                rel: 'canonical',
                href: '/who-are-you',
            },
        ],
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    // Mobile first
    .identity-choice {
        .highlight {
            strong {
                @include mq.screen_sm(min) {
                    margin: 0 calc(var(--joy-core-spacing-1) * -1);
                }
            }
        }

        &-cards {
            width: 100%;

            &__item {
                position: relative;
                height: 130px;
                text-decoration: none;
                color: var(--joy-color-neutral-60);
                flex: 1;
                margin-bottom: var(--joy-core-spacing-6);
                border-radius: var(--joy-core-radius-3);

                a {
                    position: absolute;
                    inset: 0;
                    border-radius: var(--joy-core-radius-3);

                    &:hover,
                    &:focus {
                        border: 2px solid var(--joy-color-secondary-50);
                    }
                }

                > .joy-panel {
                    display: flex;
                    height: 100%;
                    margin-bottom: 0;

                    > .joy-panel__body {
                        flex: 1;
                        display: flex;
                        align-items: center;

                        > * {
                            margin: 0 !important;
                        }
                    }

                    img {
                        width: 80px;
                        margin-right: var(--joy-core-spacing-6);
                    }
                }
            }

            &__content {
                display: flex;
                flex-direction: column;
                justify-content: center;

                h2 {
                    margin-bottom: var(--joy-core-spacing-1);
                    font-size: var(--joy-font-size-primary-600);
                }
            }

            @include mq.screen_sm(min) {
                display: flex;
                gap: var(--joy-core-spacing-6);

                &__item {
                    height: 300px;

                    > .joy-panel {
                        > .joy-panel__body {
                            flex-direction: column;
                            text-align: center;
                        }

                        img {
                            width: 125px;
                            margin-right: 0;
                            align-self: center;
                        }
                    }
                }

                &__img {
                    height: 150px;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    margin-bottom: var(--joy-core-spacing-6) !important;
                }

                &__content {
                    p {
                        font-size: var(--joy-font-size-primary-600);
                    }
                }
            }
        }

        &__link {
            a {
                color: var(--joy-color-secondary-30);
                text-decoration: none;

                &:hover,
                &:focus {
                    text-decoration: underline;
                }
            }
        }
    }
</style>
